import { useState, useEffect } from 'react';
import { getLimitedCardsWithSets, getAllLimitedCardsForAdminWithSets } from '../../../services/tracker-limited-data';
import { useAuth } from '../../../contexts/AuthContext';
import { isUserAdmin } from '../../../services/tracker-limited-data';

export const useLimitedTierlistData = () => {
    const [cards, setCards] = useState([]);
    const [filteredCards, setFilteredCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRarity, setSelectedRarity] = useState('all');
    const [selectedInkColor, setSelectedInkColor] = useState('all');
    const [selectedSet, setSelectedSet] = useState('Fabled');
    const [selectedCost, setSelectedCost] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [commonUncommonOnly, setCommonUncommonOnly] = useState(false);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
    const [sortConfig, setSortConfig] = useState({ key: 'sort_number', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const cardsPerPage = 100;
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkingAdmin, setCheckingAdmin] = useState(true);

    const { currentUser } = useAuth();

    // Sprawdź czy użytkownik jest adminem
    useEffect(() => {
        const checkAdminStatus = async () => {
            if (currentUser) {
                try {
                    const adminStatus = await isUserAdmin(currentUser.uid);
                    setIsAdmin(adminStatus);
                } catch (err) {
                    console.error('Błąd podczas sprawdzania statusu administratora:', err);
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
            setCheckingAdmin(false);
        };

        checkAdminStatus();
    }, [currentUser]);

    useEffect(() => {
        if (!checkingAdmin) {
            fetchCards();
        }
    }, [checkingAdmin, isAdmin]);

    useEffect(() => {
        filterAndSortCards();
        setCurrentPage(1); // Reset to first page when filters change
    }, [cards, searchTerm, selectedRarity, selectedInkColor, selectedSet, selectedCost, sortConfig, commonUncommonOnly]);

    const fetchCards = async () => {
        try {
            setLoading(true);

            // Jeśli użytkownik jest adminem, pobierz wszystkie karty ze wszystkich setów
            // W przeciwnym razie pobierz tylko karty z aktywnych setów
            const cardsData = isAdmin
                ? await getAllLimitedCardsForAdminWithSets()
                : await getLimitedCardsWithSets();

            // Mapuj nową strukturę danych na starą kompatybilną strukturę
            const mappedCards = cardsData.map(card => ({
                ...card,
                // Mapuj variants na image_urls dla kompatybilności
                image_urls: card.variants?.map(variant => ({
                    url: variant.detail_image_url
                })) || []
                // set jest już poprawnie ustawiony przez funkcję Firebase
            }));
            setCards(mappedCards);
            setError(null);
        } catch (err) {
            console.error('Error fetching cards:', err);
            console.log('Próbuję fallback - pobieram karty bez setów...');

            try {
                // Fallback - użyj Firebase z kartami połączonymi z setami
                const {
                    getLimitedCardsWithSets: getLimitedCardsWithSetsFallback,
                    getAllLimitedCardsForAdminWithSets: getAllLimitedCardsForAdminWithSetsFallback
                } = await import('../../../firebase/firestore');
                const fallbackCardsData = isAdmin
                    ? await getAllLimitedCardsForAdminWithSetsFallback()
                    : await getLimitedCardsWithSetsFallback();
                const mappedFallbackCards = fallbackCardsData.map(card => ({
                    ...card,
                    image_urls: card.variants?.map(variant => ({
                        url: variant.detail_image_url
                    })) || []
                }));
                setCards(mappedFallbackCards);
                setError(null);
            } catch (fallbackErr) {
                console.error('Fallback failed:', fallbackErr);
                setError('Failed to fetch cards. Check your internet connection.');
            }
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortCards = () => {
        let filtered = [...cards];

        // Filter by name/subtitle
        if (searchTerm) {
            filtered = filtered.filter(card => {
                const fullName = `${card.name}${card.subtitle ? ` - ${card.subtitle}` : ''}`;
                return fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }

        // Filter by rarity
        if (selectedRarity !== 'all') {
            filtered = filtered.filter(card => card.rarity === selectedRarity);
        }

        // Filter Common + Uncommon only
        if (commonUncommonOnly) {
            filtered = filtered.filter(card =>
                card.rarity === 'COMMON' ||
                card.rarity === 'UNCOMMON'
            );
        }

        // Filter by ink color
        if (selectedInkColor !== 'all') {
            filtered = filtered.filter(card => card.magic_ink_colors && card.magic_ink_colors.includes(selectedInkColor));
        }

        // Filter by set
        if (selectedSet !== 'all') {
            filtered = filtered.filter(card => card.set && card.set.name === selectedSet);
        }

        // Filter by cost
        if (selectedCost !== 'all') {
            filtered = filtered.filter(card => {
                const cardCost = card.ink_cost;
                if (selectedCost === '0') {
                    return cardCost === 0;
                } else if (selectedCost === '8+') {
                    return cardCost >= 8;
                } else {
                    return cardCost === parseInt(selectedCost);
                }
            });
        }

        // Sort
        filtered = sortData(filtered, sortConfig);

        setFilteredCards(filtered);
    };

    const sortData = (data, config) => {
        return [...data].sort((a, b) => {
            let aValue, bValue;

            switch (config.key) {
                case 'sort_number':
                    aValue = a.sort_number || 0;
                    bValue = b.sort_number || 0;
                    break;
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'rarity':
                    // Definiujemy kolejność rarity (COMMON, UNCOMMON, RARE, SUPER_RARE, LEGENDARY, SPECIAL)
                    const rarityOrder = { 'COMMON': 1, 'UNCOMMON': 2, 'RARE': 3, 'SUPER_RARE': 4, 'LEGENDARY': 5, 'SPECIAL': 6 };
                    aValue = rarityOrder[a.rarity] || 999;
                    bValue = rarityOrder[b.rarity] || 999;
                    break;
                case 'magic_ink_colors':
                    aValue = a.magic_ink_colors ? a.magic_ink_colors.join(',') : '';
                    bValue = b.magic_ink_colors ? b.magic_ink_colors.join(',') : '';
                    break;
                case 'rating':
                    aValue = a.rating || 0;
                    bValue = b.rating || 0;
                    break;
                default:
                    aValue = a[config.key];
                    bValue = b[config.key];
            }

            if (aValue < bValue) {
                return config.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return config.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const handleCardHover = (card, event) => {
        setHoveredCard(card);
        setHoverPosition({ x: event.clientX, y: event.clientY });
    };

    const handleCardLeave = () => {
        setHoveredCard(null);
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Calculate pagination
    const totalPages = Math.ceil(filteredCards.length / cardsPerPage);
    const startIndex = (currentPage - 1) * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    const currentCards = filteredCards.slice(startIndex, endIndex);

    // Get unique sets and rarities for filters
    const uniqueSets = cards
        .map(card => card.set?.name)  // Tylko nazwy setów
        .filter(Boolean)
        .filter((name, index, arr) => arr.indexOf(name) === index)  // Usuń duplikaty
        .sort((a, b) => {
            // Znajdź sort_number dla każdej nazwy setu
            const cardA = cards.find(card => card.set?.name === a);
            const cardB = cards.find(card => card.set?.name === b);
            const sortNumberA = cardA?.set?.sort_number || 0;
            const sortNumberB = cardB?.set?.sort_number || 0;
            return sortNumberA - sortNumberB;
        });
    const uniqueRarities = [...new Set(cards.map(card => card.rarity).filter(Boolean))].sort((a, b) => {
        // Definiujemy kolejność rarity (COMMON, UNCOMMON, RARE, SUPER_RARE, LEGENDARY, SPECIAL)
        const rarityOrder = { 'COMMON': 1, 'UNCOMMON': 2, 'RARE': 3, 'SUPER_RARE': 4, 'LEGENDARY': 5, 'SPECIAL': 6 };
        const aValue = rarityOrder[a] || 999;
        const bValue = rarityOrder[b] || 999;
        return aValue - bValue;
    });

    // Get unique costs for filters with grouping for 8+
    const allCosts = cards.map(card => card.ink_cost).filter(cost => cost !== undefined);
    const uniqueCosts = [];

    // Dodaj koszty 0-7 osobno
    for (let cost = 0; cost <= 7; cost++) {
        if (allCosts.includes(cost)) {
            uniqueCosts.push(cost);
        }
    }

    // Sprawdź czy są karty o koszcie 8+
    const hasHighCostCards = allCosts.some(cost => cost >= 8);
    if (hasHighCostCards) {
        uniqueCosts.push('8+');
    }

    return {
        cards,
        filteredCards,
        loading,
        error,
        searchTerm,
        selectedRarity,
        selectedInkColor,
        selectedSet,
        showFilters,
        hoveredCard,
        hoverPosition,
        sortConfig,
        currentPage,
        cardsPerPage,
        totalPages,
        currentCards,
        uniqueSets,
        uniqueRarities,
        uniqueCosts,
        isAdmin,
        setSearchTerm,
        setSelectedRarity,
        setSelectedInkColor,
        setSelectedSet,
        setSelectedCost,
        setShowFilters,
        setHoveredCard,
        setHoverPosition,
        setSortConfig,
        setCurrentPage,
        commonUncommonOnly,
        setCommonUncommonOnly,
        handleCardHover,
        handleCardLeave,
        handlePageChange
    };
}; 